/**
 * Whether a question is aligned with a section's blueprint: one answer.
 *
 * The question sheet reached it three ways. Assessment details checked a
 * coverage matrix of saved category-by-Bloom cells, Add question checked saved
 * gaps, and the Blueprint column and the editor checked the section plan. So
 * one question could read Aligned in the table and Not aligned in the sheet,
 * and the sheet's own wording differed by page.
 *
 * Everything now asks the section plan, through the same check the Blueprint
 * column uses, and words the answer the same way.
 */

import {
  paperQuestionBlueprintExplain,
  type PaperBlueprintSubject,
} from "@/lib/leo-blueprint-section-fill"
import { blueprintSubjectOfBankRow } from "@/lib/blueprint/replacement"
import type { AssessmentBankQuestion } from "@/lib/mock/assessment-add-questions"

export type BlueprintAlignment = { aligned: boolean; detail: string }

/**
 * Null when the section has no blueprint targets: there is nothing to be
 * aligned or not aligned with, and the sheet says nothing.
 */
export function blueprintAlignmentFor(
  sectionId: string | undefined,
  row: AssessmentBankQuestion,
): BlueprintAlignment | null {
  return blueprintAlignmentForSubject(
    sectionId,
    blueprintSubjectOfBankRow(row),
    [row.topic, row.bloom],
  )
}

/** The same answer for a row already in the paper's own vocabulary. */
export function blueprintAlignmentForSubject(
  sectionId: string | undefined,
  subject: PaperBlueprintSubject,
  current?: ReadonlyArray<string | undefined>,
): BlueprintAlignment | null {
  if (!sectionId) return null
  const explain = paperQuestionBlueprintExplain(sectionId, subject)
  if (explain.checks.length === 0) return null
  if (explain.aligned) {
    return {
      aligned: true,
      detail: "Aligned with this section's blueprint on every target.",
    }
  }
  const here = (current ?? [subject.topic, subject.blooms])
    .filter(Boolean)
    .join(" · ")
  return {
    aligned: false,
    detail: `Not aligned with this section's blueprint on ${explain.summary
      .replace(/^Not aligned on /, "")
      .replace(/\.$/, "")}.${here ? ` Current: ${here}.` : ""}`,
  }
}
